import { XbotStatusEvent } from "@dto/pyxbot-agent.dto";
import { Label } from "src/data/Label";
import { FC, useEffect, useState } from "react";
import { LuCheck, LuLoaderCircle } from "react-icons/lu";
import { stepText, withStep, XbotTurnProgress } from "./turnState";

export const STILL_WORKING_AFTER_MS = 10_000;

export function elapsedText(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) {
    return `${seconds} s`;
  }
  return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
}

export interface XbotProgressBarProps {
  /** null before the turn has been accepted */
  progress?: XbotTurnProgress | null;
  /** defaults to progress.startedAt, else to when the bar mounted */
  since?: number;
}

/** After this long the wait also says how to stop the answer. */
export const STOP_HINT_AFTER_MS = 30_000;
/** The most steps the wait lists: the step now and the ones just before it, so the bubble does not grow. */
export const STEPS_SHOWN = 3;

/** Who is slow, said after STILL_WORKING_AFTER_MS: the product while it is asked for data, the answer otherwise; named as the steps name it. */
export function slowText(phase: XbotStatusEvent | null | undefined): string {
  return phase?.phase === "waiting_ng" || phase?.phase === "calling_tool"
    ? `${Label.XORMON_NG} is taking longer than usual.`
    : "Big questions can take a minute.";
}

export const STOP_HINT = "You can stop it with the stop button below.";

/** The steps the wait lists, the last being the step now: from the turn, else the step now alone. */
export function shownSteps(progress: XbotTurnProgress | null | undefined): string[] {
  return withStep(progress?.steps, stepText(progress?.phase)).slice(-STEPS_SHOWN);
}

// The steps one under another, the done ones quiet with a tick, the step
// now with a turning circle and the time; a slow answer says who is slow, and later how to stop it.
export const XbotProgressBar: FC<XbotProgressBarProps> = ({ progress, since }) => {
  const [started] = useState(() => since ?? progress?.startedAt ?? Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const elapsed = Math.max(0, now - started);
  const steps = shownSteps(progress);
  const last = steps.length - 1;
  return <div className="xm-xbot-phase xm-xbot-progress xm-xbot-steps" role="status" aria-live="polite">
    <ul className="xm-xbot-steps-list">
      {steps.map((step, index) => (index < last
        ? <li key={index} className="is-done"><LuCheck aria-hidden="true" />{step}</li>
        : <li key={index} className="is-now">
          <LuLoaderCircle aria-hidden="true" className="xm-xbot-steps-turn" />
          <span className="xm-xbot-progress-step">{step}</span>
          <span className="xm-xbot-progress-time">{elapsedText(elapsed)}</span>
        </li>))}
    </ul>
    {elapsed >= STILL_WORKING_AFTER_MS ? <div className="xm-xbot-steps-slow">{slowText(progress?.phase)}</div> : null}
    {elapsed >= STOP_HINT_AFTER_MS ? <div className="xm-xbot-steps-slow">{STOP_HINT}</div> : null}
  </div>;
};
